import React, { useRef, useEffect } from "react";
import { CiFileOn, CiFolderOn } from "react-icons/ci";
import { renderToStaticMarkup } from "react-dom/server";

const CanvasAnimation = ({ type }) => {
    const canvasRef = useRef(null);

    const iconToImage = (IconComponent, size = 32) => {
        const svgMarkup = renderToStaticMarkup(<IconComponent size={size} />);
        const svgBlob = new Blob([svgMarkup], { type: "image/svg+xml" });
        const url = URL.createObjectURL(svgBlob);
        const img = new Image();
        img.src = url;
        return img;
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;

        let animationFrameId;
        let angle = 0;

        if (type === "cloud" && (!canvas.fileImg || !canvas.folderImg)) {
            canvas.fileImg = iconToImage(CiFileOn, 32);
            canvas.folderImg = iconToImage(CiFolderOn, 32);
        }

        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            if (type === "nosql") {
                const nodes = [];
                const nodeCount = 70;
                const maxDistance = 70;

                if (!canvas.nodes) {
                    for (let i = 0; i < nodeCount; i++) {
                        nodes.push({
                            x: Math.random() * canvas.width,
                            y: Math.random() * canvas.height,
                            vx: (Math.random() - 0.5) * 0.5,
                            vy: (Math.random() - 0.5) * 0.5
                        });
                    }
                    canvas.nodes = nodes;
                } else {
                    canvas.nodes.forEach(node => {
                        node.x += node.vx;
                        node.y += node.vy;

                        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
                        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;
                    });
                }

                for (let i = 0; i < canvas.nodes.length; i++) {
                    for (let j = i + 1; j < canvas.nodes.length; j++) {
                        const a = canvas.nodes[i];
                        const b = canvas.nodes[j];
                        const dx = a.x - b.x;
                        const dy = a.y - b.y;
                        const dist = Math.sqrt(dx * dx + dy * dy);
                        if (dist < maxDistance) {
                            ctx.strokeStyle = "rgba(128,128,128," + (1 - dist / maxDistance) + ")";
                            ctx.lineWidth = 1;
                            ctx.beginPath();
                            ctx.moveTo(a.x, a.y);
                            ctx.lineTo(b.x, b.y);
                            ctx.stroke();
                        }
                    }
                }

                canvas.nodes.forEach(node => {
                    ctx.beginPath();
                    ctx.arc(node.x, node.y, 3, 0, Math.PI * 2);
                    ctx.fillStyle = "gray";
                    ctx.fill();
                });
            } else if (type === "vector") {
                const size = 100; // size of the cube
                const cx = canvas.width / 2;
                const cy = canvas.height / 2;
                const rotation = angle * 0.5;

                const project = (x, y, z) => {
                    const scale = 400 / (z + 600); // simple perspective
                    return {
                        x: cx + x * scale,
                        y: cy + y * scale
                    };
                };

                // Define cube corners in 3D
                const cubePoints = [
                    [-size, -size, -size],
                    [size, -size, -size],
                    [size, size, -size],
                    [-size, size, -size],
                    [-size, -size, size],
                    [size, -size, size],
                    [size, size, size],
                    [-size, size, size],
                ];

                // Rotate cube and project to 2D
                const rotated = cubePoints.map(([x, y, z]) => {
                    const rx = x * Math.cos(rotation) - z * Math.sin(rotation);
                    const rz = x * Math.sin(rotation) + z * Math.cos(rotation);
                    const ry = y;
                    return project(rx, ry, rz);
                });

                // Draw cube edges
                const edges = [
                    [0, 1], [1, 2], [2, 3], [3, 0],
                    [4, 5], [5, 6], [6, 7], [7, 4],
                    [0, 4], [1, 5], [2, 6], [3, 7],
                ];
                ctx.strokeStyle = "gray";
                ctx.lineWidth = 1;
                edges.forEach(([i, j]) => {
                    ctx.beginPath();
                    ctx.moveTo(rotated[i].x, rotated[i].y);
                    ctx.lineTo(rotated[j].x, rotated[j].y);
                    ctx.stroke();
                });

                // Draw vector points
                if (!canvas.vectorPoints) {
                    const pts = [];
                    for (let i = 0; i < 70; i++) {
                        pts.push({
                            x: (Math.random() - 0.5) * size * 1.5,
                            y: (Math.random() - 0.5) * size * 1.5,
                            z: (Math.random() - 0.5) * size * 1.5,
                            pulse: Math.random() > 0.8,
                        });
                    }
                    canvas.vectorPoints = pts;
                }

                canvas.vectorPoints.forEach((pt, idx) => {
                    const rx = pt.x * Math.cos(rotation) - pt.z * Math.sin(rotation);
                    const rz = pt.x * Math.sin(rotation) + pt.z * Math.cos(rotation);
                    const ry = pt.y;

                    const pos = project(rx, ry, rz);

                    const r = pt.pulse ? 2.5 + Math.sin(angle * 4 + idx) : 2;
                    ctx.beginPath();
                    ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
                    ctx.fillStyle = pt.pulse ? "gray" : "#888";
                    ctx.fill();
                });
            } else if (type === "api") {

                const gridSize = 3;
                const spacing = 200;
                const centerX = canvas.width / 2 - spacing;
                const centerY = canvas.height / 2 - spacing;

                if (!canvas.apiNodes) {
                    canvas.apiNodes = [];
                    canvas.apiLinks = [];

                    // Generate grid of nodes
                    for (let y = 0; y < gridSize; y++) {
                        for (let x = 0; x < gridSize; x++) {
                            canvas.apiNodes.push({
                                x: centerX + x * spacing,
                                y: centerY + y * spacing,
                            });
                        }
                    }

                    // Randomly connect some nodes (simulate endpoints communication)
                    for (let i = 0; i < 40; i++) {
                        const a = Math.floor(Math.random() * canvas.apiNodes.length);
                        const b = Math.floor(Math.random() * canvas.apiNodes.length);
                        if (a !== b) {
                            canvas.apiLinks.push({
                                from: canvas.apiNodes[a],
                                to: canvas.apiNodes[b],
                                offset: Math.random() * 100,
                            });
                        }
                    }
                }

                // Draw nodes
                canvas.apiNodes.forEach(node => {
                    ctx.beginPath();
                    ctx.arc(node.x, node.y, 6, 0, Math.PI * 2);
                    ctx.fillStyle = "#999";
                    ctx.fill();
                });

                // Animate lines and packets
                canvas.apiLinks.forEach((link, index) => {
                    const { from, to, offset } = link;

                    // Draw line
                    ctx.beginPath();
                    ctx.moveTo(from.x, from.y);
                    ctx.lineTo(to.x, to.y);
                    ctx.strokeStyle = "gray";
                    ctx.lineWidth = 2;
                    ctx.stroke();

                    // Draw packet moving along the line
                    const t = ((angle * 40 + offset) % 100) / 100;
                    const x = from.x + (to.x - from.x) * t;
                    const y = from.y + (to.y - from.y) * t;

                    ctx.beginPath();
                    ctx.arc(x, y, 3, 0, Math.PI * 2);
                    ctx.fillStyle = "#666";
                    ctx.fill();
                });
            } else if (type === "cloud") {
                if (!canvas.cloudBlocks) {
                    canvas.cloudBlocks = Array.from({ length: 20 }, () => ({
                        baseX: Math.random() * canvas.width,
                        baseY: Math.random() * canvas.height,
                        radius: 20 + Math.random() * 10,
                        offsetX: Math.random() * 2 * Math.PI,
                        offsetY: Math.random() * 2 * Math.PI,
                        isGrouped: Math.random() > 0.6
                    }));
                }

                canvas.cloudBlocks.forEach((block, i) => {
                    const { baseX, baseY, offsetX, offsetY, isGrouped } = block;
                    const x = baseX + Math.sin(angle + offsetX) * 8;
                    const y = baseY + Math.cos(angle + offsetY) * 8;

                    const size = 24;
                    const icon = isGrouped ? canvas.folderImg : canvas.fileImg;

                    if (icon?.complete) {
                        ctx.drawImage(icon, x - size / 2, y - size / 2, size, size);
                    }

                    if (isGrouped && i % 2 === 0) {
                        const partner = canvas.cloudBlocks[(i + 3) % canvas.cloudBlocks.length];
                        const px = partner.baseX + Math.sin(angle + partner.offsetX) * 8;
                        const py = partner.baseY + Math.cos(angle + partner.offsetY) * 8;
                        ctx.beginPath();
                        ctx.moveTo(x, y);
                        ctx.lineTo(px, py);
                        ctx.strokeStyle = "rgba(120,120,120,0.2)";
                        ctx.stroke();
                    }
                });
            } else if (type === "rabac") {
                const cx = canvas.width / 2;
                const cy = canvas.height / 2;

                // Initialize nodes once
                if (!canvas.rabacNodes) {
                    canvas.rabacNodes = Array.from({ length: 8 }, (_, i) => {
                        const angleOffset = (Math.PI * 2 * i) / 8;
                        return {
                            angleOffset,
                            radius: 50 + Math.random() * 30,
                            isRole: Math.random() > 0.5
                        };
                    });
                }

                // Draw center resource
                ctx.beginPath();
                ctx.arc(cx, cy, 8, 0, Math.PI * 2);
                ctx.fillStyle = "gray";
                ctx.fill();

                canvas.rabacNodes.forEach((node, i) => {
                    const orbitRadius = node.radius + Math.sin(angle + i) * 3;
                    const x = cx + Math.cos(node.angleOffset + angle * 0.3) * orbitRadius;
                    const y = cy + Math.sin(node.angleOffset + angle * 0.3) * orbitRadius;

                    // Draw node
                    ctx.beginPath();
                    ctx.arc(x, y, node.isRole ? 6 : 4, 0, Math.PI * 2);
                    ctx.fillStyle = node.isRole ? "#444" : "#888";
                    ctx.fill();

                    const pulse = Math.sin(angle * 2 + i);
                    if (pulse > 0.2) {
                        ctx.beginPath();
                        ctx.moveTo(cx, cy);
                        ctx.lineTo(x, y);
                        ctx.strokeStyle = "rgba(100, 100, 100, 0.4)";
                        ctx.stroke();
                    }
                });
            } else if (type === "autoscale") {
                const barCount = 10;
                const barWidth = canvas.width / (barCount * 1.5);
                const spacing = barWidth * 1.5;

                for (let i = 0; i < barCount; i++) {
                    const x = spacing * i + barWidth / 2;
                    const maxBarHeight = canvas.height * 0.6;
                    const load = (Math.sin(angle + i) + 1) / 2; // 0 to 1
                    const height = load * maxBarHeight;
                    const y = canvas.height - height;

                    // Color changes based on load
                    const color = `rgba(120, 120, 120, ${0.4 + load * 0.6})`;
                    ctx.fillStyle = color;
                    ctx.fillRect(x, y, barWidth, height);

                    // Optional: draw base line (threshold)
                    ctx.beginPath();
                    ctx.moveTo(0, canvas.height - maxBarHeight / 2);
                    ctx.lineTo(canvas.width, canvas.height - maxBarHeight / 2);
                    ctx.strokeStyle = "rgba(100,100,100,0.1)";
                    ctx.stroke();
                }
            } else if (type === "backup") {
                const ctx = canvas.getContext("2d");
                const width = canvas.width;
                const height = canvas.height;

                const nodeCount = 20;
                const clusterRadius = 3;
                const maxDistance = 100;

                const containerWidth = 150;
                const containerHeight = 150;
                const padding = 40;

                const leftBox = {
                    x: width / 2 - containerWidth - padding,
                    y: height / 2 - containerHeight / 2,
                    w: containerWidth,
                    h: containerHeight
                };

                const rightBox = {
                    x: width / 2 + padding,
                    y: height / 2 - containerHeight / 2,
                    w: containerWidth,
                    h: containerHeight
                };

                const initNodes = (box) =>
                    Array.from({ length: nodeCount }, () => ({
                        x: box.x + Math.random() * box.w,
                        y: box.y + Math.random() * box.h,
                        vx: (Math.random() - 0.5) * 0.5,
                        vy: (Math.random() - 0.5) * 0.5,
                    }));

                if (!canvas.backupClusters) {
                    canvas.backupClusters = {
                        left: initNodes(leftBox),
                        right: initNodes(rightBox),
                    };
                }

                const drawContainer = (box) => {
                    ctx.strokeStyle = "rgba(180, 180, 180, 0.5)";
                    ctx.lineWidth = 1.5;
                    ctx.setLineDash([]);
                    ctx.strokeRect(box.x, box.y, box.w, box.h);
                };

                const updateAndDrawCluster = (nodes, box) => {
                    nodes.forEach((node) => {
                        node.x += node.vx;
                        node.y += node.vy;

                        // bounce within the container
                        if (node.x < box.x || node.x > box.x + box.w) node.vx *= -1;
                        if (node.y < box.y || node.y > box.y + box.h) node.vy *= -1;
                    });

                    for (let i = 0; i < nodes.length; i++) {
                        for (let j = i + 1; j < nodes.length; j++) {
                            const a = nodes[i];
                            const b = nodes[j];
                            const dx = a.x - b.x;
                            const dy = a.y - b.y;
                            const dist = Math.sqrt(dx * dx + dy * dy);
                            if (dist < maxDistance) {
                                ctx.strokeStyle = `rgba(120, 120, 120, ${1 - dist / maxDistance})`;
                                ctx.beginPath();
                                ctx.moveTo(a.x, a.y);
                                ctx.lineTo(b.x, b.y);
                                ctx.stroke();
                            }
                        }
                    }

                    nodes.forEach((node) => {
                        ctx.beginPath();
                        ctx.arc(node.x, node.y, clusterRadius, 0, Math.PI * 2);
                        ctx.fillStyle = "#777";
                        ctx.fill();
                    });
                };

                // Draw containers
                drawContainer(leftBox);
                drawContainer(rightBox);

                // Draw connection between container centers
                const centerLeft = { x: leftBox.x + leftBox.w / 2, y: leftBox.y + leftBox.h / 2 };
                const centerRight = { x: rightBox.x + rightBox.w / 2, y: rightBox.y + rightBox.h / 2 };

                ctx.beginPath();
                ctx.setLineDash([2, 4]);
                ctx.moveTo(centerLeft.x, centerLeft.y);
                ctx.lineTo(centerRight.x, centerRight.y);
                ctx.strokeStyle = "rgba(120, 120, 120, 0.6)";
                ctx.lineWidth = 1;
                ctx.stroke();
                ctx.setLineDash([]);

                // Draw clusters inside containers
                updateAndDrawCluster(canvas.backupClusters.left, leftBox);
                updateAndDrawCluster(canvas.backupClusters.right, rightBox);
            } else if (type === "network") {
                const nodeCount = 10;
                const center = { x: canvas.width / 2, y: canvas.height / 2 };

                if (!canvas.networkNodes) {
                    // Place nodes in a circular layout
                    canvas.networkNodes = Array.from({ length: nodeCount }, (_, i) => {
                        const angle = (Math.PI * 2 * i) / nodeCount;
                        return {
                            x: center.x + Math.cos(angle) * 80,
                            y: center.y + Math.sin(angle) * 80,
                            angle,
                        };
                    });

                    canvas.networkLinks = [];
                    for (let i = 0; i < nodeCount; i++) {
                        for (let j = i + 1; j < nodeCount; j++) {
                            if (Math.random() > 0.6) {
                                canvas.networkLinks.push({
                                    from: canvas.networkNodes[i],
                                    to: canvas.networkNodes[j],
                                    offset: Math.random() * 100,
                                });
                            }
                        }
                    }
                }

                // Draw links (pulsing)
                canvas.networkLinks.forEach((link, i) => {
                    const { from, to, offset } = link;
                    const pulse = (Math.sin(angle * 2 + offset) + 1) / 2; // 0 to 1
                    ctx.strokeStyle = `rgba(150,150,150,${0.2 + 0.5 * pulse})`;
                    ctx.lineWidth = 1 + pulse;
                    ctx.beginPath();
                    ctx.moveTo(from.x, from.y);
                    ctx.lineTo(to.x, to.y);
                    ctx.stroke();

                    // Simulate traffic as moving dots
                    const t = ((angle * 40 + offset) % 100) / 100;
                    const x = from.x + (to.x - from.x) * t;
                    const y = from.y + (to.y - from.y) * t;

                    ctx.beginPath();
                    ctx.arc(x, y, 2, 0, Math.PI * 2);
                    ctx.fillStyle = "#777";
                    ctx.fill();
                });

                // Draw nodes
                canvas.networkNodes.forEach(node => {
                    ctx.beginPath();
                    ctx.arc(node.x, node.y, 6, 0, Math.PI * 2);
                    ctx.fillStyle = "#888";
                    ctx.fill();
                });

                // Optional: Central node
                ctx.beginPath();
                ctx.arc(center.x, center.y, 8, 0, Math.PI * 2);
                ctx.fillStyle = "#444";
                ctx.fill();
            } else if (type === "projects") {
                const canvas = canvasRef.current;
                const ctx = canvas.getContext("2d");

                let width = canvas.width = window.innerWidth;
                let height = canvas.height = window.innerHeight;
                const cx = width / 2;
                const cy = height / 2;
                const radius = Math.min(width, height) / 2.1;


                const dotsCount = 10000;
                const dots = [];

                for (let i = 0; i < dotsCount; i++) {
                    let x, y, z;
                    do {
                        x = Math.random() * 2 - 1;
                        y = Math.random() * 2 - 1;
                        z = Math.random() * 2 - 1;
                    } while (x * x + y * y + z * z > 1);

                    dots.push({
                        x, y, z,
                        screenX: 0,
                        screenY: 0,
                        offsetX: 0,
                        offsetY: 0,
                        velX: 0,
                        velY: 0,
                    });
                }

                let angle = 0;

                function rotateY(point, angle) {
                    const cosA = Math.cos(angle);
                    const sinA = Math.sin(angle);
                    const x = point.x * cosA - point.z * sinA;
                    const z = point.x * sinA + point.z * cosA;
                    return { x, y: point.y, z };
                }

                function project(point3d) {
                    const distance = 3;
                    const z = distance + point3d.z;
                    const scale = distance / z;
                    return {
                        x: cx + point3d.x * radius * scale,
                        y: cy + point3d.y * radius * scale,
                        scale,
                    };
                }

                function drawSphere() {
                    ctx.clearRect(0, 0, width, height);

                    // Plain black background or transparent (change as you want)
                    ctx.fillStyle = "transparent"; // Or "transparent"
                    ctx.fillRect(0, 0, width, height);

                    // No sphere lines or gradients, just dots

                    dots.forEach(dot => {
                        const rotated = rotateY(dot, angle);
                        const proj = project(rotated);

                        dot.screenX = proj.x;
                        dot.screenY = proj.y;

                        dot.velX *= 0.85;
                        dot.velY *= 0.85;

                        dot.offsetX += dot.velX;
                        dot.offsetY += dot.velY;

                        const spring = 0.1;
                        dot.offsetX -= dot.offsetX * spring;
                        dot.offsetY -= dot.offsetY * spring;
                    });

                    // Draw dots as crisp white dots
                    ctx.fillStyle = "black";
                    dots.forEach(dot => {
                        ctx.beginPath();
                        ctx.arc(dot.screenX + dot.offsetX, dot.screenY + dot.offsetY, 1.5, 0, Math.PI * 2);
                        ctx.fill();
                    });

                    angle += 0.003;
                    animationFrameId = requestAnimationFrame(drawSphere);
                }

                drawSphere();

                const handleResize = () => {
                    width = canvas.width = window.innerWidth;
                    height = canvas.height = window.innerHeight;
                };
                window.addEventListener("resize", handleResize);

                return () => {
                    window.removeEventListener("resize", handleResize);
                    cancelAnimationFrame(animationFrameId);
                };
            } else if (type === "json") {
                if (!canvas.jsonStructure) {
                    const rootX = canvas.width / 2;
                    const rootY = 5;
                    const levelSpacing = 35;
                    const siblingSpacing = 40;

                    let nodeId = 0;

                    // Build a mock JSON structure
                    const mockJson = {
                        user: {
                            name: "Alice",
                            address: {
                                city: "Wonderland",
                                zip: "12345"
                            }
                        },
                        items: ["apple", "banana", "carrot"]
                    };

                    const nodes = [];
                    const edges = [];

                    function buildStructure(obj, x, y, depth, parent = null) {
                        const currentNode = { id: nodeId++, x, y, parent };
                        nodes.push(currentNode);

                        const children = [];

                        if (typeof obj === "object" && obj !== null) {
                            const keys = Array.isArray(obj) ? obj : Object.keys(obj);
                            const count = keys.length;
                            const totalWidth = (count - 1) * siblingSpacing;
                            keys.forEach((key, i) => {
                                const childX = x - totalWidth / 2 + i * siblingSpacing;
                                const childY = y + levelSpacing;
                                const childValue = Array.isArray(obj) ? obj[key] : obj[key];
                                const child = buildStructure(childValue, childX, childY, depth + 1, currentNode);
                                children.push(child);
                                edges.push({ from: currentNode, to: child });
                            });
                        }

                        return currentNode;
                    }

                    const rootNode = buildStructure(mockJson, rootX, rootY, 0);
                    canvas.jsonStructure = { nodes, edges };

                    // Build traversal path
                    const path = [];
                    function traverse(node) {
                        path.push({ x: node.x, y: node.y });
                        canvas.jsonStructure.edges
                            .filter(e => e.from === node)
                            .forEach(e => traverse(e.to));
                    }
                    traverse(rootNode);
                    canvas.jsonPath = path;
                }

                const { nodes, edges } = canvas.jsonStructure;

                // Draw lines
                ctx.strokeStyle = "#aaa";
                ctx.lineWidth = 1;
                edges.forEach(({ from, to }) => {
                    ctx.beginPath();
                    ctx.moveTo(from.x, from.y);
                    ctx.lineTo(to.x, to.y);
                    ctx.stroke();
                });

                // Draw nodes (dots)
                nodes.forEach(node => {
                    ctx.beginPath();
                    ctx.arc(node.x, node.y, 4, 0, Math.PI * 2);
                    ctx.fillStyle = "#666";
                    ctx.fill();
                });

                // Animate traveling dot
                // if (canvas.jsonPath.length > 1) {
                //     const travel = (angle * 2) % (canvas.jsonPath.length - 1);
                //     const index = Math.floor(travel);
                //     const t = travel - index;

                //     const from = canvas.jsonPath[index];
                //     const to = canvas.jsonPath[index + 1];

                //     const x = from.x + (to.x - from.x) * t;
                //     const y = from.y + (to.y - from.y) * t;

                //     ctx.beginPath();
                //     ctx.arc(x, y, 5, 0, Math.PI * 2);
                //     ctx.fillStyle = "#000";
                //     ctx.fill();
                // }
            } else if (type === "logs") {
                const startX = canvas.width / 2;
                const startY = 5;
                const spacing = 35;
                const count = 3;

                for (let i = 0; i < count; i++) {
                    const x = startX;
                    const y = startY + i * spacing;

                    // Draw dot
                    ctx.beginPath();
                    ctx.arc(x, y, 5, 0, Math.PI * 2);
                    ctx.fillStyle = "#444";
                    ctx.fill();

                    // Draw line to next dot
                    if (i < count - 1) {
                        const nextY = y + spacing;
                        ctx.beginPath();
                        ctx.moveTo(x, y);
                        ctx.lineTo(x, nextY);
                        ctx.strokeStyle = "#999";
                        ctx.lineWidth = 2;
                        ctx.stroke();
                    }
                }
            } else if (type === "clusters") {
                // Setup clusters if not already created
                if (!canvas.clusters) {
                    const clusters = [];

                    // Tighter triangle cluster centers
                    clusters.push({
                        centerX: canvas.width / 2,
                        centerY: canvas.height * 0.4, // moved down a bit
                        nodes: createNodes(10, 40),
                        rotation: 0,
                    });

                    clusters.push({
                        centerX: canvas.width / 2 - 70, // moved closer to center
                        centerY: canvas.height * 0.7,  // moved up a bit
                        nodes: createNodes(10, 40),
                        rotation: 0,
                    });

                    clusters.push({
                        centerX: canvas.width / 2 + 70, // moved closer to center
                        centerY: canvas.height * 0.7,  // moved up a bit
                        nodes: createNodes(10, 40),
                        rotation: 0,
                    });


                    canvas.clusters = clusters;
                }

                // Helper function to create cluster nodes
                function createNodes(nodeCount, radius) {
                    const nodes = [];
                    for (let i = 0; i < nodeCount; i++) {
                        nodes.push({
                            baseAngle: (Math.PI * 2 * i) / nodeCount,
                            radius,
                            size: 4 + Math.random() * 2,
                            pulsePhase: Math.random() * Math.PI * 2,
                        });
                    }
                    return nodes;
                }

                ctx.clearRect(0, 0, canvas.width, canvas.height);

                // Drawing logic stays the same as your existing code, no changes needed here

                canvas.clusters.forEach(cluster => {
                    cluster.rotation += 0.005; // slow rotation for nodes in cluster

                    const points = cluster.nodes.map(node => {
                        const angle = node.baseAngle + cluster.rotation;
                        const pulse = 2 * Math.sin(angle * 5 + node.pulsePhase);
                        const r = node.radius + pulse;
                        const x = cluster.centerX + Math.cos(angle) * r;
                        const y = cluster.centerY + Math.sin(angle) * r;
                        return { x, y, size: node.size };
                    });


                    // Draw nodes
                    points.forEach(pt => {
                        ctx.beginPath();
                        ctx.arc(pt.x, pt.y, pt.size, 0, Math.PI * 2);
                        ctx.fillStyle = "gray";
                        ctx.shadowColor = "lightgray";
                        ctx.shadowBlur = 6;
                        ctx.fill();
                        ctx.shadowBlur = 0;
                    });

                    // Optionally draw cluster center softly
                    const gradient = ctx.createRadialGradient(cluster.centerX, cluster.centerY, 0, cluster.centerX, cluster.centerY, 10);
                    gradient.addColorStop(0, "rgba(150,150,150,0.5)");
                    gradient.addColorStop(1, "rgba(150,150,150,0)");
                    ctx.fillStyle = gradient;
                    ctx.beginPath();
                    ctx.arc(cluster.centerX, cluster.centerY, 10, 0, Math.PI * 2);
                    ctx.fill();
                });

                // Optional: connect clusters softly if close
                for (let i = 0; i < canvas.clusters.length; i++) {
                    for (let j = i + 1; j < canvas.clusters.length; j++) {
                        const a = canvas.clusters[i];
                        const b = canvas.clusters[j];
                        const dx = a.centerX - b.centerX;
                        const dy = a.centerY - b.centerY;
                        const dist = Math.sqrt(dx * dx + dy * dy);
                        if (dist < 200) {
                            ctx.strokeStyle = `rgba(200,200,200,${1 - dist / 200})`;
                            ctx.lineWidth = 1;
                            ctx.beginPath();
                            ctx.moveTo(a.centerX, a.centerY);
                            ctx.lineTo(b.centerX, b.centerY);
                            ctx.stroke();
                        }
                    }
                }
            } else if (type === "playbox") {
                const ideCount = 1;
                const codeLinesPerPage = 7;

                if (!canvas.idePages) {
                    canvas.idePages = Array.from({ length: ideCount }).map((_, i) => {
                        const pageWidth = canvas.width - 60; // leave 10px margin on each side
                        const pageHeight = canvas.height;
                        const x = 30; // left margin
                        const y = 20;

                        const codeBlocks = Array.from({ length: codeLinesPerPage }).map((_, j) => ({
                            offsetX: 20,
                            offsetY: 20 + j * 20,
                            baseWidth: pageWidth - 40, // 20px padding left + right
                            height: 5,
                            pulseOffset: Math.random() * Math.PI * 1
                        }));

                        return { x, y, width: pageWidth, height: pageHeight, codeBlocks };
                    });
                }

                canvas.idePages.forEach(page => {
                    // Draw outer IDE frame
                    // ctx.save();
                    // ctx.fillStyle = "white";
                    // ctx.strokeStyle = "#ccc";
                    // ctx.lineWidth = 0.0;
                    // ctx.shadowColor = "lightgrey";
                    // ctx.shadowBlur = 0;
                    // ctx.beginPath();
                    // ctx.roundRect(page.x, page.y, page.width, page.height, 15);
                    // ctx.fill();
                    // ctx.stroke();
                    // ctx.restore();

                    // Draw animated "typing" code lines
                    page.codeBlocks.forEach(block => {
                        const pulse = Math.sin(angle + block.pulseOffset);
                        const widthProgress = (pulse + 1) / 2; // normalize to 0–1
                        const animatedWidth = block.baseWidth * widthProgress;

                        ctx.save();
                        ctx.fillStyle = "grey";
                        ctx.strokeStyle = "grey";
                        ctx.lineWidth = 0;
                        ctx.shadowColor = "lightgrey";
                        ctx.shadowBlur = 0;
                        ctx.beginPath();
                        ctx.roundRect(
                            page.x + block.offsetX,
                            page.y + block.offsetY,
                            animatedWidth,
                            block.height,
                            4
                        );
                        ctx.fill();
                        ctx.stroke();
                        ctx.restore();
                    });
                });
            } else if (type === "deployment") {
                const canvas = canvasRef.current;
                const ctx = canvas.getContext("2d");

                let width = canvas.width = window.innerWidth;
                let height = canvas.height = window.innerHeight;
                const cx = width / 2;
                const cy = height / 2;
                const radius = Math.min(width, height) / 2;

                const dotsCount = 1000;
                const dots = [];

                for (let i = 0; i < dotsCount; i++) {
                    let x, y, z;
                    do {
                        x = Math.random() * 2 - 1;
                        y = Math.random() * 2 - 1;
                        z = Math.random() * 2 - 1;
                    } while (x * x + y * y + z * z > 1);

                    dots.push({
                        x, y, z,
                        screenX: 0,
                        screenY: 0,
                        offsetX: 0,
                        offsetY: 0,
                        velX: 0,
                        velY: 0,
                        color: Math.random() < 0.1 ? "#2e3242" : "black",
                        pulsePhase: Math.random() * Math.PI * 2,  // random pulse offset
                    });
                }

                let angle = 0;

                function rotateY(point, angle) {
                    const cosA = Math.cos(angle);
                    const sinA = Math.sin(angle);
                    const x = point.x * cosA - point.z * sinA;
                    const z = point.x * sinA + point.z * cosA;
                    return { x, y: point.y, z };
                }

                function project(point3d) {
                    const distance = 3;
                    const z = distance + point3d.z;
                    const scale = distance / z;
                    return {
                        x: cx + point3d.x * radius * scale,
                        y: cy + point3d.y * radius * scale,
                        scale,
                    };
                }

                const ripples = [];

                function drawSphere() {
                    ctx.clearRect(0, 0, width, height);
                    ctx.fillStyle = "transparent";
                    ctx.fillRect(0, 0, width, height);

                    dots.forEach(dot => {
                        const rotated = rotateY(dot, angle);
                        const proj = project(rotated);

                        dot.screenX = proj.x;
                        dot.screenY = proj.y;

                        dot.velX *= 0.85;
                        dot.velY *= 0.85;

                        dot.offsetX += dot.velX;
                        dot.offsetY += dot.velY;

                        const spring = 0.1;
                        dot.offsetX -= dot.offsetX * spring;
                        dot.offsetY -= dot.offsetY * spring;
                    });

                    dots.forEach(dot => {
                        let radius = 1.5;
                        let alpha = 1;

                        if (dot.color === "#2e3242") {
                            // Pulse calculation
                            const pulse = (Math.sin(angle * 10 + dot.pulsePhase) + 1) / 2;

                            // Trigger ripple at pulse peak
                            if (dot.lastPulse !== undefined) {
                                if (dot.lastPulse < 0.95 && pulse >= 0.95) {
                                    ripples.push({
                                        x: dot.screenX + dot.offsetX,
                                        y: dot.screenY + dot.offsetY,
                                        radius: 0,
                                        maxRadius: 60,
                                        wavesCount: 3,
                                        spacing: 15,
                                    });
                                }
                            }
                            dot.lastPulse = pulse;

                            radius = 8 + pulse * 2;
                            alpha = 0.5 + pulse * 0.5;
                        }

                        ctx.beginPath();
                        ctx.fillStyle = dot.color;
                        ctx.globalAlpha = alpha;
                        ctx.arc(dot.screenX + dot.offsetX, dot.screenY + dot.offsetY, radius, 0, Math.PI * 2);
                        ctx.fill();
                        ctx.globalAlpha = 1;
                    });

                    // Draw ripples (shockwaves)
                    for (let i = ripples.length - 1; i >= 0; i--) {
                        const ripple = ripples[i];
                        ctx.strokeStyle = `rgba(46, 50, 66, 0.4)`; // base color with some alpha
                        ctx.lineWidth = 2;

                        for (let w = 0; w < ripple.wavesCount; w++) {
                            const r = ripple.radius - w * ripple.spacing;
                            if (r > 0) {
                                const alpha = Math.max(0, 1 - r / ripple.maxRadius);
                                ctx.globalAlpha = alpha * 0.6;
                                ctx.beginPath();
                                ctx.arc(ripple.x, ripple.y, r, 0, Math.PI * 2);
                                ctx.stroke();
                            }
                        }
                        ctx.globalAlpha = 1;

                        ripple.radius += 1.5;
                        if (ripple.radius - ripple.wavesCount * ripple.spacing > ripple.maxRadius) {
                            ripples.splice(i, 1);
                        }
                    }

                    angle += 0.003;
                    animationFrameId = requestAnimationFrame(drawSphere);
                }


                drawSphere();

                const handleResize = () => {
                    width = canvas.width = window.innerWidth;
                    height = canvas.height = window.innerHeight;
                };
                window.addEventListener("resize", handleResize);

                return () => {
                    window.removeEventListener("resize", handleResize);
                    cancelAnimationFrame(animationFrameId);
                };
            } else if (type === "sqltable") {
                if (!canvas.sqlTableStructure) {
                    const rootX = 60;
                    const rootY = 20;
                    const cellWidth = 70;
                    const cellHeight = 20;
                    const rowSpacing = 5;
                    const colSpacing = 5;

                    const columns = ["", "", ""];
                    const numRows = 3;

                    const nodes = [];

                    for (let row = 0; row <= numRows; row++) {
                        for (let col = 0; col < columns.length; col++) {
                            const x = rootX + col * (cellWidth + colSpacing);
                            const y = rootY + row * (cellHeight + rowSpacing);
                            const text = row === 0 ? columns[col] : "";
                            nodes.push({ x, y, width: cellWidth, height: cellHeight, text });
                        }
                    }

                    canvas.sqlTableStructure = nodes;
                }

                const nodes = canvas.sqlTableStructure;
                ctx.strokeStyle = "#000000ff";
                ctx.fillStyle = "#fff";
                ctx.lineWidth = 0.1;

                nodes.forEach(node => {
                    ctx.fillStyle = node.text !== "" ? "#ddd" : "#fff";
                    ctx.fillRect(node.x, node.y, node.width, node.height);
                    ctx.strokeRect(node.x, node.y, node.width, node.height);
                    if (node.text) ctx.fillText(node.text, node.x + node.width / 2, node.y + node.height / 2);
                });
            }



            angle += 0.02;
            animationFrameId = requestAnimationFrame(draw);
        };

        const resizeCanvas = () => {
            // canvas may disappear during routing/resizing
            if (!canvasRef.current) return;
            const canvasEl = canvasRef.current;

            const parent = canvasEl.parentElement;
            if (!parent) return;

            const rect = parent.getBoundingClientRect();

            canvasEl.width = rect.width;
            canvasEl.height = rect.height;

            draw(); // <-- SAFE now
        };

        resizeCanvas();
        window.addEventListener("resize", resizeCanvas);

        return () => cancelAnimationFrame(animationFrameId);
    }, [type]);

    return <div className="canvas_container">
        <canvas
            className="product_canvas"
            ref={canvasRef}
            style={{ width: "auto", height: "180px" }}
        />
    </div>;
};

export default CanvasAnimation